import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Plus, Loader2, X, Gift } from 'lucide-react';
import api from '../../services/api';

const DEFAULT_YEARS = [1, 2, 3];

const WAAnniversaryFlowCreatePage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [yearTemplates, setYearTemplates] = useState(DEFAULT_YEARS.map(y => ({ year: y, template_id: '' })));
  const [newYearInput, setNewYearInput] = useState('');

  useEffect(() => {
    api.get('/wa/templates').then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const addYear = () => {
    const y = parseInt(newYearInput);
    if (isNaN(y) || y < 1) return;
    if (yearTemplates.some(yt => yt.year === y)) return;
    setYearTemplates(prev => [...prev, { year: y, template_id: '' }].sort((a, b) => a.year - b.year));
    setNewYearInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const filtered = yearTemplates.filter(yt => yt.template_id);
    if (filtered.length === 0) return alert('Select at least one template');
    setSaving(true);
    try {
      const res = await api.post('/wa/anniversary-flows', { name, is_active: isActive, year_templates: filtered });
      navigate(`/whatsapp/anniversary-flows/${res.data.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating flow');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/whatsapp/fixed-flows')}>
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift className="text-[#4F46E5]" size={24} /> Create Anniversary Flow
          </h1>
        </div>
      </div>

      <Card className="bg-white border border-gray-100 p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Flow Name *</label>
            <Input placeholder="e.g., Purchase Anniversary" value={name} onChange={(e) => setName(e.target.value)} required data-testid="anniversary-flow-name-input" />
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded border-gray-300" />
            <span className="text-sm text-gray-700">Active</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Message Schedule (Years)</label>
            <div className="flex items-center gap-2 mb-4 bg-gray-50 p-3 rounded-lg">
              <span className="text-sm text-gray-600">Add Year:</span>
              <Input type="number" min="1" max="50" placeholder="e.g., 4" value={newYearInput} onChange={(e) => setNewYearInput(e.target.value)} className="w-24" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addYear(); } }} />
              <Button type="button" variant="outline" size="sm" onClick={addYear}><Plus size={14} className="mr-1" /> Add</Button>
              <span className="text-sm text-gray-400">Years since first purchase</span>
            </div>
            <div className="space-y-3">
              {yearTemplates.map((yt) => (
                <div key={yt.year} className="flex items-center gap-3">
                  <span className="text-sm font-bold text-[#4F46E5] w-16">Year {yt.year}</span>
                  <select className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm" value={yt.template_id} onChange={(e) => setYearTemplates(prev => prev.map(y => y.year === yt.year ? { ...y, template_id: e.target.value } : y))}>
                    <option value="">None</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button type="button" onClick={() => setYearTemplates(prev => prev.filter(y => y.year !== yt.year))} className="text-red-400 hover:text-red-600 p-1"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => navigate('/whatsapp/fixed-flows')}>Cancel</Button>
            <Button type="submit" disabled={saving || !name} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="submit-anniversary-flow">
              {saving && <Loader2 className="animate-spin mr-2" size={16} />}
              Create Flow
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default WAAnniversaryFlowCreatePage;
